// Hook para carregar dados de referência de alergia do banco de dados
import { useState, useEffect, useCallback } from 'react'
import AllergyReferenceService, {
  AllergyCode,
  Manifestation,
  ClinicalStatusCode,
  VerificationStatusCode
} from '../lib/allergyReferenceService'

// Interface para o estado do hook
interface AllergyReferenceState {
  allergyCodes: AllergyCode[]
  manifestations: Manifestation[]
  clinicalStatusCodes: ClinicalStatusCode[]
  verificationStatusCodes: VerificationStatusCode[]
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null
}

// Hook principal para dados de referência de alergia
export const useAllergyReference = () => {
  const [state, setState] = useState<AllergyReferenceState>({
    allergyCodes: [],
    manifestations: [],
    clinicalStatusCodes: [],
    verificationStatusCodes: [],
    isLoading: true,
    error: null,
    lastUpdated: null
  })

  // Função para carregar todos os dados
  const loadReferenceData = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const result = await AllergyReferenceService.getAllReferenceData()

      if (result.error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Erro ao carregar dados de referência de alergia'
        }))
        return
      }

      setState({
        allergyCodes: result.allergyCodes,
        manifestations: result.manifestations,
        clinicalStatusCodes: result.clinicalStatusCodes,
        verificationStatusCodes: result.verificationStatusCodes,
        isLoading: false,
        error: null,
        lastUpdated: new Date()
      })
    } catch (err) {
      console.error('Erro inesperado ao carregar dados de referência:', err)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Erro inesperado ao carregar dados de referência'
      }))
    }
  }, [])

  // Carregar dados na inicialização
  useEffect(() => {
    loadReferenceData()
  }, [loadReferenceData])

  // Função para recarregar dados
  const refresh = useCallback(() => {
    loadReferenceData()
  }, [loadReferenceData])

  return {
    ...state,
    refresh
  }
}

// Hook específico para códigos de alergia
export const useAllergyCodes = (category?: string) => {
  const [allergyCodes, setAllergyCodes] = useState<AllergyCode[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadAllergyCodes = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = category
        ? await AllergyReferenceService.getAllergyCodesByCategory(category)
        : await AllergyReferenceService.getAllergyCodes()

      if (result.error) {
        setError('Erro ao carregar códigos de alergia')
        setAllergyCodes([])
      } else {
        setAllergyCodes(result.data || [])
      }
    } catch (err) {
      console.error('Erro inesperado ao carregar códigos de alergia:', err)
      setError('Erro inesperado ao carregar códigos de alergia')
      setAllergyCodes([])
    } finally {
      setIsLoading(false)
    }
  }, [category])

  useEffect(() => {
    loadAllergyCodes()
  }, [loadAllergyCodes])

  return {
    allergyCodes,
    isLoading,
    error,
    refresh: loadAllergyCodes
  }
}

// Hook específico para manifestações
export const useManifestations = (category?: string) => {
  const [manifestations, setManifestations] = useState<Manifestation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadManifestations = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = category
        ? await AllergyReferenceService.getManifestationsByCategory(category)
        : await AllergyReferenceService.getManifestations()

      if (result.error) {
        setError('Erro ao carregar manifestações')
        setManifestations([])
      } else {
        setManifestations(result.data || [])
      }
    } catch (err) {
      console.error('Erro inesperado ao carregar manifestações:', err)
      setError('Erro inesperado ao carregar manifestações')
      setManifestations([])
    } finally {
      setIsLoading(false)
    }
  }, [category])

  useEffect(() => {
    loadManifestations()
  }, [loadManifestations])

  return {
    manifestations,
    isLoading,
    error,
    refresh: loadManifestations
  }
}

// Hook para busca de códigos específicos
export const useAllergyCodeSearch = () => {
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  const searchAllergyCode = useCallback(async (searchTerm: string): Promise<AllergyCode | null> => {
    if (!searchTerm.trim()) return null

    setIsSearching(true)
    setSearchError(null)

    try {
      const result = await AllergyReferenceService.findAllergyCode(searchTerm)

      if (result.error) {
        setSearchError('Erro ao buscar código de alergia')
        return null
      }

      return result.data
    } catch (err) {
      console.error('Erro inesperado ao buscar código de alergia:', err)
      setSearchError('Erro inesperado ao buscar código de alergia')
      return null
    } finally {
      setIsSearching(false)
    }
  }, [])

  const searchManifestation = useCallback(async (searchTerm: string): Promise<Manifestation | null> => {
    if (!searchTerm.trim()) return null

    setIsSearching(true)
    setSearchError(null)

    try {
      const result = await AllergyReferenceService.findManifestation(searchTerm)

      if (result.error) {
        setSearchError('Erro ao buscar manifestação')
        return null
      }

      return result.data
    } catch (err) {
      console.error('Erro inesperado ao buscar manifestação:', err)
      setSearchError('Erro inesperado ao buscar manifestação')
      return null
    } finally {
      setIsSearching(false)
    }
  }, [])

  return {
    searchAllergyCode,
    searchManifestation,
    isSearching,
    searchError
  }
}

// Hook para status codes (clínico e verificação)
export const useStatusCodes = () => {
  const [clinicalStatusCodes, setClinicalStatusCodes] = useState<ClinicalStatusCode[]>([])
  const [verificationStatusCodes, setVerificationStatusCodes] = useState<VerificationStatusCode[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadStatusCodes = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [clinicalResult, verificationResult] = await Promise.all([
        AllergyReferenceService.getClinicalStatusCodes(),
        AllergyReferenceService.getVerificationStatusCodes()
      ])

      if (clinicalResult.error || verificationResult.error) {
        setError('Erro ao carregar códigos de status')
        setClinicalStatusCodes([])
        setVerificationStatusCodes([])
      } else {
        setClinicalStatusCodes(clinicalResult.data || [])
        setVerificationStatusCodes(verificationResult.data || [])
      }
    } catch (err) {
      console.error('Erro inesperado ao carregar códigos de status:', err)
      setError('Erro inesperado ao carregar códigos de status')
      setClinicalStatusCodes([])
      setVerificationStatusCodes([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatusCodes()
  }, [loadStatusCodes])

  return {
    clinicalStatusCodes,
    verificationStatusCodes,
    isLoading,
    error,
    refresh: loadStatusCodes
  }
}